import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Power, MapPin, RefreshCw, AlertCircle } from "lucide-react";
import { api, setToken } from "@/lib/api";
import OfficerShell, { ROLE_LABEL } from "./OfficerShell";

const SUBDIVISIONS = ["Amravati", "Tivasa", "Chandur Railway", "Dharni", "Daryapur", "Morshi", "Achalpur"];
const TEHSILS = ["Amravati", "Bhatkuli", "Nandgaon Khandeshwar", "Chandur Railway", "Dhamangaon Railway", "Tiwsa", "Morshi", "Warud", "Chandur Bazar", "Achalpur", "Anjangaon Surji", "Daryapur", "Dharni", "Chikhaldara"];

export default function OfficerAdmin() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await api.officerAccounts();
      setAccounts(Array.isArray(data) ? data : []);
    } catch (e) {
      setAccounts([]);
      setLoadError(e?.message || "खाती लोड करता आली नाहीत.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const reLogin = () => { setToken(null); navigate("/officer"); };

  const resetPw = async (a) => {
    const custom = window.prompt(`खाते '${a.username}' साठी नवीन पासवर्ड (रिकामे = default Amravati@2026):`, "");
    if (custom === null) return;
    try {
      const res = await api.resetAccountPw(a.id, custom ? { newPassword: custom } : {});
      setMsg(`'${a.username}' का पासवर्ड रीसेट: ${res.temporaryPassword} (प्रथम लॉगिनला बदलावा लागेल)`);
      load();
    } catch (e) { setMsg(e.message); }
  };
  const toggle = async (a) => {
    try { await api.toggleAccount(a.id); load(); } catch (e) { setMsg(e.message); }
  };
  const reassign = async (a, value) => {
    if (!value) return;
    try { await api.reassignAccount(a.id, { jurisdiction: value }); setMsg(`'${a.username}' चे कार्यक्षेत्र बदलले: ${value}`); load(); }
    catch (e) { setMsg(e.message); }
  };

  return (
    <OfficerShell>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-head text-[24px] font-medium text-[#1b5e3f]">खाते व्यवस्थापन (जिल्हाधिकारी)</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/officer/credentials")} data-testid="open-credentials-sheet" className="flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-[#144d33]"><KeyRound className="h-4 w-4" /> क्रेडेन्शियल शीट</button>
          <button onClick={load} className="flex items-center gap-1.5 rounded-full border border-[#1b5e3f]/30 px-3.5 py-1.5 text-[13px] font-semibold text-[#1b5e3f] hover:bg-[#1b5e3f]/5"><RefreshCw className="h-4 w-4" /> पुन्हा लोड</button>
        </div>
      </div>
      {msg && <div className="mb-4 rounded-lg border-l-4 border-[#E0A106] bg-[#fdf7e5] px-4 py-2.5 text-[13.5px] text-[#5c4a1c]">{msg}</div>}
      <div className="overflow-x-auto rounded-2xl border border-[#e6dcc4] bg-white">
        <table className="w-full text-[13.5px]">
          <thead><tr className="border-b border-slate-200 bg-[#fbf9f2] text-left text-slate-500">
            <th className="p-3">वापरकर्तानाव</th><th className="p-3">पद / नाव</th><th className="p-3">कार्यक्षेत्र</th><th className="p-3">स्थिती</th><th className="p-3">कृती</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400">खाती लोड होत आहेत...</td></tr>
            ) : loadError ? (
              <tr><td colSpan={5} className="p-8 text-center">
                <div className="flex flex-col items-center gap-3 text-slate-500">
                  <AlertCircle className="h-8 w-8 text-red-400" />
                  <p className="text-[14px] font-semibold text-red-600">खाती लोड करता आली नाहीत</p>
                  <p className="max-w-md text-[13px]">{loadError} — सत्र कालबाह्य झाले असल्यास कृपया पुन्हा लॉगिन करा.</p>
                  <div className="flex gap-2">
                    <button onClick={load} className="rounded-full border border-[#1b5e3f]/30 px-4 py-1.5 text-[13px] font-semibold text-[#1b5e3f] hover:bg-[#1b5e3f]/5">पुन्हा प्रयत्न करा</button>
                    <button onClick={reLogin} className="rounded-full bg-[#1b5e3f] px-4 py-1.5 text-[13px] font-semibold text-white hover:bg-[#144d33]">पुन्हा लॉगिन करा</button>
                  </div>
                </div>
              </td></tr>
            ) : accounts.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400">कोणतीही अधिकारी खाती आढळली नाहीत.</td></tr>
            ) : accounts.map((a) => (
              <tr key={a.id} className="border-b border-slate-100">
                <td className="p-3 font-latin font-semibold text-[#1b3d2a]">{a.username}</td>
                <td className="p-3"><span className="font-semibold text-[#1b5e3f]">{ROLE_LABEL[a.role]}</span><br/><span className="text-[12px] text-slate-500">{a.name}</span></td>
                <td className="p-3">
                  {a.role === "collector" || a.role === "rdc" ? <span className="text-slate-400">संपूर्ण जिल्हा</span> : (
                    <select defaultValue={a.jurisdiction || ""} onChange={(e) => reassign(a, e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-[13px] outline-none focus:border-[#1b5e3f]">
                      <option value="" disabled>निवडा</option>
                      {(a.role === "sdo" ? SUBDIVISIONS : TEHSILS).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                </td>
                <td className="p-3">
                  {a.active === false ? <span className="rounded-full bg-red-100 px-2.5 py-1 text-[12px] font-semibold text-red-700">निष्क्रिय</span> : <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[12px] font-semibold text-emerald-700">सक्रिय</span>}
                  {a.mustChangePassword && <span className="ml-1 rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-800">PW बदल बाकी</span>}
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => resetPw(a)} className="flex items-center gap-1 rounded-full border border-slate-300 px-2.5 py-1 text-[12px] font-semibold text-slate-600 hover:border-[#1b5e3f] hover:text-[#1b5e3f]"><KeyRound className="h-3.5 w-3.5" /> पासवर्ड रीसेट</button>
                    {a.role !== "collector" && <button onClick={() => toggle(a)} className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-semibold ${a.active === false ? "border-emerald-400 text-emerald-700" : "border-red-300 text-red-600"}`}><Power className="h-3.5 w-3.5" /> {a.active === false ? "सक्रिय करा" : "निष्क्रिय"}</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12.5px] text-slate-400">एकूण {accounts.length} अधिकारी खाती · कार्यक्षेत्र बदलल्यास संबंधित अर्जांचा आवाका लगेच अपडेट होतो.</p>
    </OfficerShell>
  );
}
